

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { SHEET_INITIAL, SHEET_ANIMATE, SHEET_EXIT, SHEET_SPRING } from "@/components/Modal/sheetMotion";
import { IconExternalLink, IconX } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { useTranslations } from "next-intl";

import { Card } from "../Card/Card";
import { useUserLocale } from "@/hooks/useUserLocale";
import { useEventTimezone } from "@/providers/EventProvider";
import { getTranslatedWarningMessage } from "@/lib/slotting/utils";
import type {
  Appointment,
  AppointmentStatus,
  StatusDotRule,
} from "@/lib/slotting/types";
import type { Guest } from "@/types/Guest";
import { matchStatusDots } from "@/lib/slotting/statusDots";
import { StatusDots } from "@/components/slotting/StatusDots";
import { APPOINTMENT_STATUS_COLORS } from "@/lib/slotting/statusColors";
import { useSuppressCoach } from "@/lib/onboarding/useSuppressCoach";

interface PAppointmentDetailsProps {
  appointment: Appointment;
  eventId: string;
  offeringName?: string;
  treatmentName?: string;
  laneNames?: string[];
  /** guestId → display name, resolved by the parent. Falls back to the id. */
  guestNames?: Map<string, string>;
  /** Offering status-dot rules + guest data, both resolved by the parent
   *  (this popup renders outside the slotting board/store). Absent ⇒ no dots. */
  statusDotRules?: StatusDotRule[];
  guestRecords?: Map<string, Guest>;
  onClose: () => void;
}

const STATUS_TRANSLATION_KEY: Record<AppointmentStatus, string> = {
  TENTATIVE: "statusTentative",
  CONFIRMED: "statusConfirmed",
  CHECKED_IN: "statusCheckedIn",
  CANCELLED: "statusCancelled",
};

// startDt/endDt are real UTC instants — render in the event tz so the booked
// slot reads the same for every admin regardless of their browser tz.
function formatDate(dt: string, locale: string, tz: string): string {
  try {
    return new Date(dt).toLocaleDateString(locale, {
      timeZone: tz,
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dt;
  }
}

function formatTimeRange(
  startDt: string,
  endDt: string,
  locale: string,
  tz: string,
): string {
  try {
    const fmt = (d: Date) =>
      d.toLocaleTimeString(locale, {
        timeZone: tz,
        hour: "2-digit",
        minute: "2-digit",
      });
    return `${fmt(new Date(startDt))} – ${fmt(new Date(endDt))}`;
  } catch {
    return "";
  }
}

export const PAppointmentDetails: React.FC<PAppointmentDetailsProps> = ({
  appointment,
  eventId,
  offeringName,
  treatmentName,
  laneNames,
  guestNames,
  statusDotRules,
  guestRecords,
  onClose,
}) => {
  const t = useTranslations("slotting");
  useSuppressCoach(); // hide bottom-right floaters over this modal
  const locale = useUserLocale();
  const tz = useEventTimezone();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  if (!mounted) return null;

  const statusColor = APPOINTMENT_STATUS_COLORS[appointment.status] ?? APPOINTMENT_STATUS_COLORS.TENTATIVE;
  const statusKey = STATUS_TRANSLATION_KEY[appointment.status] ?? "statusTentative";

  const activeWarnings = (appointment.computedWarnings ?? []).filter(
    (w) => !w.dismissed,
  );

  const dateStr = appointment.startDt?.split("T")[0];
  const calendarHref = `/event/${eventId}/slotting/${appointment.offeringId}?appointmentId=${appointment.appointmentId}${dateStr ? `&date=${dateStr}` : ""}`;

  return createPortal(
    <motion.div
      className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center p-0 pt-4 sm:p-8 z-[60] sm:pr-[calc(2rem+var(--ai-rail-push))] transition-[padding] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <motion.div
        className="w-full sm:max-w-[560px] flex flex-col min-h-0 max-h-full"
        initial={SHEET_INITIAL}
        animate={SHEET_ANIMATE}
        exit={SHEET_EXIT}
        transition={SHEET_SPRING}
      >
        <Card
          padding="p-6"
          surfaceColor="surface-card"
          rounded="rounded-t-3xl sm:rounded-3xl"
          width="w-full"
          height="h-auto max-h-full"
          className="z-50 overflow-hidden"
          justify="gap-5"
          header={t("appointmentDetails.title")}
          icon={IconX}
          iconActionOnClick={onClose}
        >
          <div className="flex flex-col gap-4 overflow-y-auto min-h-0 flex-1">
            {/* Status + time */}
            <div className="flex flex-row items-center justify-between gap-3 flex-wrap">
              <span
                className={`inline-flex items-center px-2 py-1 rounded body-4-medium shrink-0 ${statusColor.chipBg} ${statusColor.chipFg}`}
              >
                {t(statusKey)}
              </span>
              <div className="flex flex-col items-end">
                <p className="body-2-medium">
                  {formatDate(appointment.startDt, locale, tz)}
                </p>
                <p className="body-3-regular font-tertiary">
                  {formatTimeRange(appointment.startDt, appointment.endDt, locale, tz)}
                </p>
              </div>
            </div>

            {/* Cancelled banner */}
            {appointment.cancelledAt && (
              <div className="flex flex-col gap-1 surface-tertiary px-4 py-3 rounded-lg border outline-quaternary">
                <p className="body-3-medium font-error">
                  {t("appointmentDetails.cancelled")}
                </p>
                {appointment.cancelledReason && (
                  <p className="body-4-regular font-tertiary">
                    {appointment.cancelledReason}
                  </p>
                )}
              </div>
            )}

            {/* Detail grid */}
            <div className="flex flex-col gap-3">
              <DetailRow
                label={t("appointmentDetails.offering")}
                value={offeringName ?? appointment.offeringId}
              />
              <DetailRow
                label={t("appointmentDetails.treatment")}
                value={treatmentName ?? "—"}
              />
              <DetailRow
                label={t("appointmentDetails.lanes")}
                value={
                  laneNames && laneNames.length > 0
                    ? laneNames.join(", ")
                    : "—"
                }
              />
              {appointment.notes && (
                <DetailRow
                  label={t("note")}
                  value={appointment.notes}
                  multiline
                />
              )}
            </div>

            {/* Guests */}
            {appointment.guests && appointment.guests.length > 0 && (
              <div className="flex flex-col gap-2">
                <p className="body-3-medium font-secondary">
                  {t("appointmentDetails.guests")} ({appointment.guests.length})
                </p>
                <ul className="flex flex-col gap-1">
                  {appointment.guests.map((g) => (
                    <li
                      key={g.guestId}
                      className="flex flex-row items-center justify-between surface-tertiary px-3 py-2 rounded-lg body-3-regular"
                    >
                      <span className="font-secondary truncate">
                        {g.guestDisplayName || guestNames?.get(g.guestId) || g.guestId}
                        {g.isCompanion && (
                          <span className="body-4-regular font-tertiary ml-2">
                            ({t("appointmentDetails.companion")})
                          </span>
                        )}
                        {statusDotRules && guestRecords && (
                          <StatusDots
                            dots={matchStatusDots(statusDotRules, guestRecords.get(g.guestId))}
                            className="inline-flex items-center gap-0.5 ml-2 align-middle"
                          />
                        )}
                      </span>
                      {g.checkInAt && (
                        <span className="body-4-regular chip-green-fg shrink-0">
                          {t("appointmentDetails.checkedIn")}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Warnings */}
            {activeWarnings.length > 0 && (
              <div className="flex flex-col gap-2">
                <p className="body-3-medium font-secondary">
                  {t("appointmentDetails.warnings")}
                </p>
                <ul className="flex flex-col gap-1">
                  {activeWarnings.map((w, i) => (
                    <li
                      key={i}
                      className="chip-yellow-bg chip-yellow-fg px-3 py-2 rounded-lg body-4-regular"
                    >
                      {getTranslatedWarningMessage(w.type, w.message, t)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex flex-row justify-end pt-2 border-t outline-quaternary">
            <Link
              to={calendarHref}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-row items-center gap-1.5 px-3 py-2 rounded-lg border outline-quaternary body-3-medium hover:surface-secondary transition-colors"
            >
              <IconExternalLink size={14} />
              {t("appointmentDetails.openInCalendar")}
            </Link>
          </div>
        </Card>
      </motion.div>
    </motion.div>,
    document.body,
  );
};

type DetailRowProps = {
  label: string;
  value: string;
  multiline?: boolean;
};

function DetailRow({ label, value, multiline }: DetailRowProps) {
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <p className="body-4-regular font-tertiary">{label}</p>
      <p
        className={`body-3-medium ${
          multiline ? "whitespace-pre-wrap" : "truncate"
        }`}
      >
        {value}
      </p>
    </div>
  );
}
